import React from 'react';
import { Button } from './ui/button';
import { 
  Shield, Clock, ChevronRight, 
  Cloud, Mail, Video, Server,
  Search, Apple, Users, Package, AppWindow
} from 'lucide-react';

// GAFAM Cube component with icons - staggered like randomly placed cubes
const GafamCube = ({ icon: Icon, index }) => {
  // Different rotations and offsets for each cube
  const cubeStyles = [
    { rotate: '-6deg', translateY: '8px', bg: '#4285F4' },   // G - Google
    { rotate: '4deg', translateY: '-4px', bg: '#555555' },   // A - Apple
    { rotate: '-3deg', translateY: '12px', bg: '#1877F2' },  // F - Facebook
    { rotate: '7deg', translateY: '-8px', bg: '#FF9900' },   // A - Amazon
    { rotate: '-5deg', translateY: '4px', bg: '#00A4EF' },   // M - Microsoft
  ];
  
  const style = cubeStyles[index];
  
  return (
    <div 
      className="w-14 h-14 rounded-xl flex items-center justify-center text-white shadow-lg transition-transform hover:scale-110 cursor-default"
      style={{ 
        backgroundColor: style.bg,
        transform: `rotate(${style.rotate}) translateY(${style.translateY})`,
      }}
    >
      <Icon className="w-7 h-7 drop-shadow-sm" />
    </div>
  );
};

// Icons representing each GAFAM in a playful way
const gafamIcons = [
  Search,      // Google - search/magnifier
  Apple,       // Apple - apple fruit
  Users,       // Facebook/Meta - people/social
  Package,     // Amazon - package/box
  AppWindow,   // Microsoft - window
];

export const WelcomePage = ({ onStart }) => {
  return (
    <div className="animate-fade-in-up text-center" data-testid="welcome-page">
      {/* Hero section */}
      <div className="mb-8">
        <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#0d2a47] font-outfit tracking-tight mb-6">
          Évaluez votre dépendance <br className="hidden md:block" />
          aux <span className="text-[#006241]">GAFAM</span>
        </h1>

        {/* GAFAM cubes with icons - staggered like randomly placed blocks */}
        <div className="flex justify-center items-end gap-2 mb-8 h-20">
          {gafamIcons.map((Icon, i) => (
            <GafamCube key={i} icon={Icon} index={i} />
          ))}
        </div>
        
        <p className="text-lg text-slate-500 max-w-2xl mx-auto mb-8">
          Découvrez en quelques minutes votre niveau de dépendance aux géants du numérique et les alternatives souveraines pour reprendre le contrôle de vos données.
        </p>

        <Button
          onClick={onStart}
          size="lg"
          className="rounded-full px-8 py-6 bg-[#0d2a47] hover:bg-[#1a4a7a] text-white font-semibold text-lg shadow-lg hover:shadow-xl transition-all"
          data-testid="start-questionnaire-btn"
        >
          Commencer le test
          <ChevronRight className="w-5 h-5 ml-2" />
        </Button>
      </div>

      {/* Features */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="p-5 rounded-2xl bg-slate-50 border border-slate-100">
          <Clock className="w-8 h-8 text-[#0d2a47] mx-auto mb-3" />
          <h3 className="font-semibold text-[#0d2a47] mb-1">2 minutes</h3>
          <p className="text-sm text-slate-500">Questionnaire rapide</p>
        </div>
        <div className="p-5 rounded-2xl bg-slate-50 border border-slate-100">
          <Shield className="w-8 h-8 text-[#006241] mx-auto mb-3" />
          <h3 className="font-semibold text-[#0d2a47] mb-1">9 catégories</h3>
          <p className="text-sm text-slate-500">Analyse complète</p>
        </div>
        <div className="p-5 rounded-2xl bg-slate-50 border border-slate-100">
          <Cloud className="w-8 h-8 text-[#006241] mx-auto mb-3" />
          <h3 className="font-semibold text-[#0d2a47] mb-1">Alternatives</h3>
          <p className="text-sm text-slate-500">Solutions souveraines</p>
        </div>
      </div>

      {/* Categories preview */}
      <div className="text-left p-6 rounded-2xl bg-white border border-slate-100 shadow-sm">
        <h3 className="font-semibold text-[#0d2a47] mb-4 text-center">
          Services analysés
        </h3>
        <div className="flex flex-wrap justify-center gap-2">
          {[
            { icon: Mail, label: 'Email' },
            { icon: Cloud, label: 'Stockage' },
            { icon: Video, label: 'Visio' },
            { icon: Server, label: 'Cloud' },
          ].map(({ icon: Icon, label }, i) => (
            <span 
              key={i}
              className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-100 text-sm text-slate-600"
            >
              <Icon className="w-4 h-4" />
              {label}
            </span>
          ))}
          <span className="px-3 py-1.5 rounded-full bg-[#006241]/10 text-sm text-[#006241] font-medium">
            +5 autres
          </span>
        </div>
      </div>

      {/* oueb.it branding */}
      <div className="mt-8 pt-6 border-t border-slate-100">
        <p className="text-sm text-slate-400">
          Proposé par{' '}
          <a 
            href="https://oueb.it" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-[#006241] hover:underline font-medium"
          >
            oueb.it
          </a>
          {' '}- Partenaire Infomaniak
        </p>
      </div>
    </div>
  );
};

export default WelcomePage;
